/**
 * THE piece-type registry the editor's typed piece forms are built from.
 *
 * D22 makes piece types declarative studio config (a param schema, source
 * and output layers, a timeline template, default timing), snapshotted into a
 * production at launch. Until the studio-config API exists this module is a
 * static stand-in with the same shape — **the one place** the editor learns
 * piece types from, so replacing it with a registry loaded from studio
 * config is a change to this module's source, not to the forms.
 *
 * A piece row stores its type in `f.pieceType` and its parameters as leaves
 * of the open `params` namespace (`params.input`, …: one LWW register per
 * leaf, so two people editing two parameters both win — `fields.ts`).
 */

export type PieceParamKind = 'text' | 'number' | 'json';

export type PieceParamSpec = {
  /** The leaf under `params` (`input` → `params.input`). */
  readonly key: string;
  readonly label: string;
  readonly kind: PieceParamKind;
  readonly placeholder: string;
};

export type PieceTypeSpec = {
  readonly id: string;
  readonly label: string;
  /** A short tag the tree shows (CAM, VT, …). */
  readonly short: string;
  /** Written to `sourceLayer` when a piece of this type is created. */
  readonly defaultSourceLayer: string;
  readonly params: readonly PieceParamSpec[];
};

export const PIECE_TYPES: readonly PieceTypeSpec[] = [
  {
    id: 'camera',
    label: 'Camera',
    short: 'CAM',
    defaultSourceLayer: 'camera',
    params: [{ key: 'input', label: 'Input', kind: 'text', placeholder: 'CAM 1' }],
  },
  {
    id: 'vt',
    label: 'VT (clip)',
    short: 'VT',
    defaultSourceLayer: 'vt',
    params: [{ key: 'clip', label: 'Clip', kind: 'text', placeholder: 'news/opening.mxf' }],
  },
  {
    id: 'audio',
    label: 'Audio',
    short: 'AUD',
    defaultSourceLayer: 'audio',
    params: [{ key: 'track', label: 'Track', kind: 'text', placeholder: 'bed-01' }],
  },
  {
    id: 'graphics.lower-third',
    label: 'Lower third',
    short: 'L3',
    defaultSourceLayer: 'graphics',
    params: [
      { key: 'name', label: 'Name', kind: 'text', placeholder: 'Anna Berg' },
      { key: 'title', label: 'Title', kind: 'text', placeholder: 'Correspondent' },
    ],
  },
  {
    id: 'graphics.fullscreen',
    label: 'Fullscreen graphic',
    short: 'FS',
    defaultSourceLayer: 'graphics',
    params: [
      { key: 'template', label: 'Template', kind: 'text', placeholder: 'election-map' },
      { key: 'data', label: 'Data (JSON)', kind: 'json', placeholder: '{"region": "north"}' },
    ],
  },
];

/** The spec for a piece type id, or null for a type this registry does not know (the form falls back to raw params). */
export function pieceTypeSpec(id: string): PieceTypeSpec | null {
  return PIECE_TYPES.find((t) => t.id === id) ?? null;
}

/** The tag the tree shows for a piece: the registry's short name, else the raw type, else a dash. */
export function pieceTypeShort(id: string): string {
  return pieceTypeSpec(id)?.short ?? (id === '' ? '—' : id);
}
